import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, EmptyState, Input, Skeleton, Textarea,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { CheckIcon, CloseIcon, PlusIcon } from '../../components/icons'

interface ChecklistItem { label: string; required?: boolean }
interface ChecklistTemplate {
  id?: string
  name?: string
  description?: string
  items?: ChecklistItem[]
}

const CREATE_ROLES = new Set(['super_admin', 'gov_director'])

export default function Checklists() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [items, setItems] = useState<ChecklistItem[]>([{ label: '', required: true }])

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'checklists'],
    queryFn: () => api.get<{ ok: boolean; items: ChecklistTemplate[] }>('/gov/checklists'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/gov/checklists', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov', 'checklists'] })
      setShowForm(false)
      setItems([{ label: '', required: true }])
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      name: String(form.get('name') ?? '').trim(),
      description: String(form.get('description') ?? '').trim(),
      items: items.filter((it) => it.label.trim()),
    })
  }

  const templates = data?.items ?? []
  const canCreate = CREATE_ROLES.has(user?.role ?? '')

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Inspection checklists</h1>
          <p className="page-head__sub">Reusable checklist templates for site inspections.</p>
        </div>
        {canCreate && (
          <>
            <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
              {showForm ? 'Cancel' : 'New template'}
            </Button>
            <button
              type="button" className="fab"
              onClick={() => setShowForm((s) => !s)}
              aria-label={showForm ? 'Cancel' : 'New template'}
            >
              {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
            </button>
          </>
        )}
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="New checklist template" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not create that template.'}
                  </Alert>
                )}
                {/* PWA: 2 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Template', content: (
                      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                <Input label="Name" name="name" required />
                <Textarea label="Description (optional)" name="description" rows={2} />
                      </div>
                    ) },
                    { title: 'Checklist items', content: (
                <div>
                  <label className="field__label" style={{ display: 'block', marginBottom: 6 }}>Items</label>
                  <div className="stack" style={{ gap: 'var(--sp-2)' }}>
                    {items.map((it, i) => (
                      <div key={i} className="row" style={{ gap: 'var(--sp-2)' }}>
                        <div style={{ flex: 1 }}>
                          <Input
                            placeholder={`Item ${i + 1}`}
                            value={it.label}
                            onChange={(e) => setItems((cur) => cur.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)))}
                          />
                        </div>
                        <label className="radio-opt" style={{ padding: 'var(--sp-2) var(--sp-3)' }}>
                          <input
                            type="checkbox"
                            checked={it.required}
                            onChange={(e) => setItems((cur) => cur.map((x, xi) => (xi === i ? { ...x, required: e.target.checked } : x)))}
                          />
                          Required
                        </label>
                      </div>
                    ))}
                  </div>
                  <Button type="button" size="sm" variant="ghost"
                          style={{ marginTop: 'var(--sp-2)' }}
                          onClick={() => setItems((cur) => [...cur, { label: '', required: true }])}>
                    + Add item
                  </Button>
                </div>
                    ) },
                  ]}
                  footer={(
                <div className="row">
                  <Button type="submit" loading={create.isPending}>Create template</Button>
                </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : isLoading ? (
        <div className="grid grid--2"><Skeleton h={100} /><Skeleton h={100} /></div>
      ) : templates.length === 0 ? (
        <Card><CardBody><EmptyState title="No checklist templates" body="Templates created here are available when recording an inspection." /></CardBody></Card>
      ) : (
        <div className="grid grid--2">
          {templates.map((t) => (
            <Card key={t.id}>
              <CardHeader title={t.name} sub={t.description} />
              <CardBody flush>
                <div className="stack" style={{ padding: 'var(--sp-4)' }}>
                  {(t.items ?? []).map((it, i) => (
                    <div key={i} className="row" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
                      <CheckIcon size={14} />
                      <span>{it.label}</span>
                      {it.required && <Badge tone="warning">Required</Badge>}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </>
  )
}
